import {
  useMemo,
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";

import { type BoundingBoxStyleOptions } from "@/src/components/pdf-viewer/canvas-utils";
import { type ExtractionMetadata } from "@/src/models/section";

interface PDFViewerContext {
  metadata: ExtractionMetadata | null;
  setMetadata: Dispatch<SetStateAction<ExtractionMetadata | null>>;
  clearMetadata: () => void;
  activePage: number | null;
  setActivePage: Dispatch<SetStateAction<number | null>>;
  boundingBoxStyle: BoundingBoxStyleOptions;
  setBoundingBoxStyle: Dispatch<SetStateAction<BoundingBoxStyleOptions>>;
  focusedBoundingBoxIndex: number | null;
  setFocusedBoundingBoxIndex: Dispatch<SetStateAction<number | null>>;
  focusNextMetadata: () => void;
  focusPrevMetadata: () => void;
  hideNonFocusedBoxes: boolean;
  setHideNonFocusedBoxes: Dispatch<SetStateAction<boolean>>;
  toggleHideNonFocusedBoxes: () => void;
}

const PDFViewerContext = createContext<PDFViewerContext>({
  metadata: null,
  setMetadata: () => {},
  clearMetadata: () => {},
  activePage: null,
  setActivePage: () => {},
  boundingBoxStyle: {},
  setBoundingBoxStyle: () => {},
  focusedBoundingBoxIndex: null,
  setFocusedBoundingBoxIndex: () => {},
  focusNextMetadata: () => {},
  focusPrevMetadata: () => {},
  hideNonFocusedBoxes: false,
  setHideNonFocusedBoxes: () => {},
  toggleHideNonFocusedBoxes: () => {},
});

export function usePdfViewerInteractionContext(defaultBoundingBoxStyle?: BoundingBoxStyleOptions): PDFViewerContext {
  const context = useContext(PDFViewerContext);

  useEffect(() => {
    if (defaultBoundingBoxStyle) {
      context.setBoundingBoxStyle(defaultBoundingBoxStyle);
    }
  }, [defaultBoundingBoxStyle, context.setBoundingBoxStyle]);

  return context;
}

export function PDFViewerInteractionProvider({ enabled = true, children }: PropsWithChildren<{ enabled?: boolean }>) {
  const [metadata, setMetadata] = useState<ExtractionMetadata | null>(null);
  const [activePage, setActivePage] = useState<number | null>(null);
  const [boundingBoxStyle, setBoundingBoxStyle] = useState<BoundingBoxStyleOptions>({});
  const [focusedBoundingBoxIndex, setFocusedBoundingBoxIndex] = useState<number | null>(null);
  const [hideNonFocusedBoxes, setHideNonFocusedBoxes] = useState<boolean>(false);

  const clearMetadata = useCallback(() => {
    setMetadata(null);
  }, [setMetadata]);

  useEffect(() => {
    if (metadata?.bboxes && metadata.bboxes.length > 0) {
      setFocusedBoundingBoxIndex(0);
    } else {
      setFocusedBoundingBoxIndex(null);
    }
  }, [metadata]);

  const focusNextMetadata = useCallback(() => {
    setFocusedBoundingBoxIndex((prevIndex) => {
      if (!metadata?.bboxes || metadata.bboxes.length === 0) {
        return null;
      }

      const currentIndex = typeof prevIndex === "number" ? prevIndex : -1;
      const nextIndex = (currentIndex + 1) % metadata.bboxes.length;

      return nextIndex;
    });
  }, [metadata]);

  const focusPrevMetadata = useCallback(() => {
    setFocusedBoundingBoxIndex((prevIndex) => {
      if (!metadata?.bboxes || metadata.bboxes.length === 0) {
        return null;
      }

      const currentIndex = typeof prevIndex === "number" ? prevIndex : 0;
      const nextIndex = (currentIndex - 1 + metadata.bboxes.length) % metadata.bboxes.length;

      return nextIndex;
    });
  }, [metadata]);

  const toggleHideNonFocusedBoxes = useCallback(() => {
    setHideNonFocusedBoxes((prev) => !prev);
  }, []);

  const context = useMemo(() => {
    return {
      metadata: enabled ? metadata : null,
      setMetadata,
      clearMetadata,
      activePage: enabled ? activePage : null,
      setActivePage,
      boundingBoxStyle,
      setBoundingBoxStyle,
      focusedBoundingBoxIndex,
      setFocusedBoundingBoxIndex,
      focusNextMetadata,
      focusPrevMetadata,
      hideNonFocusedBoxes,
      setHideNonFocusedBoxes,
      toggleHideNonFocusedBoxes,
    };
  }, [
    metadata,
    clearMetadata,
    enabled,
    activePage,
    boundingBoxStyle,
    focusedBoundingBoxIndex,
    focusNextMetadata,
    focusPrevMetadata,
    hideNonFocusedBoxes,
    toggleHideNonFocusedBoxes,
  ]);

  return <PDFViewerContext.Provider value={context}>{children}</PDFViewerContext.Provider>;
}
